import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { api } from '../../../../services/api';

interface TripCalendarModalProps {
  open: boolean;
  onClose: () => void;
  onTripClick?: (trip: any) => void;
}

interface CalendarTrip {
  id: string;
  tripCode: string;
  startDate: string;
  endDate?: string;
  sourceCity: string;
  destinationCities: string;
  status: string;
  customerName: string;
  driver?: {
    id: number;
    firstName: string;
    lastName: string;
    mobileNumber: string;
    email: string;
    licenseNumber: string;
  } | null;
  car?: {
    id: number;
    plateNumber: string;
    carType: string;
    carModel: string;
    fuelType: string;
    capacity: number;
    mileage: number;
    manufacturedYear: string;
    fastTagId: string;
  } | null;
}

const TripCalendarModal: React.FC<TripCalendarModalProps> = ({ open, onClose, onTripClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [trips, setTrips] = useState<CalendarTrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Load trips data
  useEffect(() => {
    if (open) {
      loadTrips();
    }
  }, [open]);

  const loadTrips = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getAllTrips();
      
      // Transform the raw API data to calendar format
      const responseData = response as { data?: any[] } | any[];
      const rawTrips = Array.isArray(responseData) ? responseData : (responseData?.data || []);
      
      const transformedTrips: CalendarTrip[] = rawTrips.map((trip: any) => ({
        id: trip.id,
        tripCode: trip.tripCode,
        startDate: trip.startDatetime,
        endDate: trip.endDatetime,
        sourceCity: trip.sourceCity,
        destinationCities: trip.destinationCities,
        status: trip.tripStatus,
        customerName: trip.customer?.name || 'Unknown Customer',
        // Transform driver information
        driver: trip.driver ? {
          id: trip.driver.id,
          firstName: trip.driver.firstName || '',
          lastName: trip.driver.lastName || '',
          mobileNumber: trip.driver.mobileNumber || '',
          email: trip.driver.email || '',
          licenseNumber: trip.driver.licenseNumber || ''
        } : null,
        // Transform car information
        car: trip.car ? {
          id: trip.car.id,
          plateNumber: String(trip.car.plateNumber || ''),
          carType: String(trip.car.carType?.makeCompany || ''),
          carModel: String(trip.car.carType?.model || ''),
          fuelType: String(trip.car.carType?.fuelType || ''),
          capacity: trip.car.carType?.capacity || 0,
          mileage: trip.car.carType?.mileage || 0,
          manufacturedYear: String(trip.car.manufacturedYear || ''),
          fastTagId: String(trip.car.fastTagId || '')
        } : null
      }));
      
      setTrips(transformedTrips);
    } catch (error: any) {
      console.error('Error loading trips for calendar:', error);
      setError('Failed to load trips data');
    } finally {
      setLoading(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getTripsForDate = (date: Date) => {
    // Use local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return trips.filter(trip => {
      // Parse trip start date and extract just the date part
      const tripStartDate = new Date(trip.startDate);
      const tripStartYear = tripStartDate.getFullYear();
      const tripStartMonth = String(tripStartDate.getMonth() + 1).padStart(2, '0');
      const tripStartDay = String(tripStartDate.getDate()).padStart(2, '0');
      const tripStartDateStr = `${tripStartYear}-${tripStartMonth}-${tripStartDay}`;
      let tripEndDateStr = tripStartDateStr; // Default to start date if no end date
    if (trip.endDate) {
      const tripEndDate = new Date(trip.endDate);
      const tripEndYear = tripEndDate.getFullYear();
      const tripEndMonth = String(tripEndDate.getMonth() + 1).padStart(2, '0');
      const tripEndDay = String(tripEndDate.getDate()).padStart(2, '0');
      tripEndDateStr = `${tripEndYear}-${tripEndMonth}-${tripEndDay}`;
    }
    
    // Check if the current date falls within the trip date range
    const isWithinRange = dateStr >= tripStartDateStr && dateStr <= tripEndDateStr;
    
    // Only show trips that are confirmed, in progress, or driver assigned
    // return dateStr === tripStartDateStr && ['confirmed', 'in progress', 'driver assigned'].includes(trip.status.toLowerCase());
    const isValidStatus = ['confirmed', 'in progress', 'driver assigned'].includes(trip.status.toLowerCase());
    return isWithinRange && isValidStatus;
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending': return '#ff9800';
      case 'confirmed': return '#2196f3';
      case 'assigned': return '#9c27b0';
      case 'in_progress': return '#3f51b5';
      case 'completed': return '#4caf50';
      case 'cancelled': return '#f44336';
      case 'scheduled': return '#4caf50';
      default: return '#757575';
    }
  };

  const getCarColor = (carModel: string) => {
    const carColors: { [key: string]: string } = {
      "Dzire": "#FFD700",
      "Carens Premium": "tomato", 
      "Crysta GX 7 STR": "#1E90FF",
      "Rumion S": "#32CD32"
    };
    return carColors[carModel] || '#757575';
  };

  const handleTripClick = (trip: CalendarTrip) => {
    if (onTripClick) {
      onTripClick({
        tripId: trip.tripCode,
        tripCode: trip.tripCode,
        tripType: 'TRIP_DETAILS',
        carType: '',
        carModel: '',
        operator: '',
        id: trip.id,
        sourceCity: trip.sourceCity,
        destinationCities: trip.destinationCities,
        status: trip.status,
        startDate: trip.startDate,
        endDate: trip.endDate,
        customerName: trip.customerName
      });
      onClose(); // Close the modal after clicking a trip
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayTrips = getTripsForDate(date);
      days.push({
        date,
        day,
        trips: dayTrips,
        isToday: date.toDateString() === new Date().toDateString()
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const selectedDateTrips = selectedDate ? getTripsForDate(selectedDate) : [];
  

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { 
      hour12: true, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    return `${day}/${month}/${year}, ${time}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#1a1a1a',
          color: 'white',
          minHeight: '500px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#0891b2', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon />
          <Typography variant="h6" fontWeight="bold">
            Trip Calendar
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#2a2a2a', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress sx={{ color: '#0891b2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading trips...
            </Typography>
          </Box>
        ) : (
          <Box display="flex" gap={3}>
            {/* Calendar */}
            <Box flex={1}>
              {/* Calendar Header */}
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="between" 
                mb={1.5}
                sx={{
                  mt:1,
                  backgroundColor: 'rgba(8, 145, 178, 0.1)',
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid rgba(8, 145, 178, 0.3)'
                }}
              >
                <IconButton onClick={() => navigateMonth('prev')} sx={{ color: 'white' }}>
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h5" fontWeight="bold" sx={{ flex: 1, textAlign: 'center' }}>
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </Typography>
                <IconButton onClick={() => navigateMonth('next')} sx={{ color: 'white' }}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Legend */}
              <Box 
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  mb: 1.5
                }}
              >
                {/* <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 0.5 }}>
                  Car Model Legend:
                </Typography> */}
                <Box display="flex" flexWrap="wrap" gap={1} mb={0.5}>
                  <Typography variant="caption" fontWeight="bold" sx={{ color: '#f1f5f9' }}>
                    Car Model Legend:
                  </Typography>
                  {Object.entries({
                    "Dzire": "#FFD700",
                    "Carens Premium": "tomato", 
                    "Crysta GX 7 STR": "#1E90FF",
                    "Rumion S": "#32CD32"
                  }).map(([model, color]) => (
                    <Box key={model} display="flex" alignItems="center" gap={0.5}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: color,
                          borderRadius: '2px'
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                        {model}
                      </Typography>
                    </Box>
                  ))}
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#757575',
                        borderRadius: '2px'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                      Other/No Car
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  • Only confirmed, in progress and driver assigned trips are shown • Trips displayed on start date only
                </Typography>
              </Box>

              {/* Day Headers */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1} mb={0.5}>
                {dayNames.map(day => (
                  <Typography
                    key={day}
                    variant="body2"
                    fontWeight="bold"
                    textAlign="center"
                    color="textSecondary"
                    sx={{ p: 0.5 }}
                  >
                    {day}
                  </Typography>
                ))}
              </Box>

              {/* Calendar Grid */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
                {calendarDays.map((dayData, index) => (
                  <Paper
                    key={index}
                    sx={{
                      minHeight: '70px',
                      p: 0.8,
                      backgroundColor: dayData 
                        ? (dayData.isToday 
                          ? 'rgba(8, 145, 178, 0.2)' 
                          : 'rgba(255, 255, 255, 0.05)')
                        : 'transparent',
                      border: dayData?.trips.length 
                        ? '2px solid #0891b2' 
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: dayData ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: dayData 
                          ? 'rgba(8, 145, 178, 0.1)' 
                          : 'transparent'
                      }
                    }}
                    onClick={() => dayData && setSelectedDate(dayData.date)}
                  >
                    {dayData && (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={dayData.isToday ? "bold" : "normal"}
                          color={dayData.isToday ? "#0891b2" : "white"}
                          mb={0.5}
                        >
                          {dayData.day}
                        </Typography>
                        {dayData.trips.length > 0 && (
                          <Box>
                            {dayData.trips.map((trip, tripIndex) => {
                              // Show first 3 trips directly, then show count for remaining
                              if (tripIndex < 3) {
                                return (
                                  <Tooltip
                                    key={trip.id}
                                    title={`${trip.tripCode} - ${trip.sourceCity} to ${trip.destinationCities} - ${trip.car?.carModel || 'No Car'}`}
                                  >
                                    <Chip
                                      label={trip.tripCode}
                                      size="small"
                                      onClick={() => handleTripClick(trip)}
                                      sx={{
                                        fontSize: '0.5rem',
                                        height: '14px',
                                        backgroundColor: getCarColor(trip.car?.carModel || ''),
                                        color: 'white',
                                        mb: 0.2,
                                        display: 'block',
                                        cursor: 'pointer',
                                        width: 'auto',
                                        borderRadius: '7px',
                                        '& .MuiChip-label': {
                                          px: 0.3,
                                          textAlign: 'center'
                                        },
                                        '&:hover': {
                                          opacity: 0.8,
                                          transform: 'scale(1.02)'
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                );
                              }
                              return null;
                            })}
                            {dayData.trips.length > 3 && (
                              <Tooltip
                                title={`${dayData.trips.length - 3} more trips: ${dayData.trips.slice(3).map(t => t.tripCode).join(', ')}`}
                              >
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#0891b2',
                                    fontSize: '0.6rem',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                  onClick={() => setSelectedDate(dayData.date)}
                                >
                                  +{dayData.trips.length - 3} more
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Trip Details Sidebar */}
            <Box width="280px">
              <Typography variant="h6" fontWeight="bold" mb={1.5} sx={{ color: '#f1f5f9' }}>
                {selectedDate 
                  ? `Trips on - ${selectedDate.toLocaleDateString('en-GB')}`
                  : 'Select a date to view trips'
                }
              </Typography>

              {selectedDate && selectedDateTrips.length === 0 && (
                <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                  No trips scheduled for this date.
                </Typography>
              )}

              {selectedDate && selectedDateTrips.map(trip => (
                <Paper
                  key={trip.id}
                  onClick={() => handleTripClick(trip)}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    border: '1px solid rgba(99, 179, 237, 0.4)',
                    cursor: 'pointer',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(51, 65, 85, 1)',
                      borderColor: '#22d3ee',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(34, 211, 238, 0.4)'
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#f8fafc' }}>
                      {trip.tripCode}
                    </Typography>
                    <Chip
                      label={trip.car?.carModel || trip.status}
                      size="small"
                      sx={{
                        backgroundColor: getCarColor(trip.car?.carModel || ''),
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>
                    Customer: <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{trip.customerName}</span>
                  </Typography>
                  <Typography variant="body2" mb={1} sx={{ color: '#ffffff', fontWeight: 500 }}>
                    {trip.sourceCity} → {trip.destinationCities}
                  </Typography>
                  
                  {/* Driver Information */}
                  {trip.driver ? (
                    <Typography variant="body2" sx={{ color: '#6ee7b7', mb: 1, fontWeight: 500 }}>
                      Driver: {`${trip.driver.firstName} ${trip.driver.lastName}`.trim()}
                      {trip.driver.mobileNumber && (
                        <Typography component="span" variant="caption" display="block" sx={{ color: '#d1d5db' }}>
                          📞 {trip.driver.mobileNumber}
                        </Typography>
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#fcd34d', mb: 1, fontWeight: 500 }}>
                      Driver: <span style={{ color: '#fef3c7' }}>Not Assigned</span>
                    </Typography>
                  )}
                  
                  {/* Car Information */}
                  {trip.car ? (
                    <Typography variant="body2" sx={{ color: '#7dd3fc', mb: 1, fontWeight: 500 }}>
                      Car: {trip.car.plateNumber || 'N/A'}
                      <Typography component="span" variant="caption" display="block" sx={{ color: '#d1d5db' }}>
                        🚗 {trip.car.carType} {trip.car.carModel} ({trip.car.manufacturedYear})
                      </Typography>
                      {trip.car.fuelType && (
                        <Typography component="span" variant="caption" display="block" sx={{ color: '#d1d5db' }}>
                          ⛽ {trip.car.fuelType} • {trip.car.capacity} Seater • {trip.car.mileage} kmpl
                        </Typography>
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#fcd34d', mb: 1, fontWeight: 500 }}>
                      Car: <span style={{ color: '#fef3c7' }}>Not Assigned</span>
                    </Typography>
                  )}
                  
                  <Typography variant="caption" sx={{ color: '#d1d5db' }}>
                    Start: {formatDateTime(trip.startDate)}
                  </Typography>
                  {trip.endDate && (
                    <Typography variant="caption" sx={{ color: '#d1d5db' }} display="block">
                      End: {formatDateTime(trip.endDate)}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: '#2a2a2a', p: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripCalendarModal;
